/**
 * Client-side image downscaling, so an oversized upload is fixed rather than
 * refused.
 *
 * A phone photo is routinely 4–8 MB. Telling the author "that image is 6.2 MB,
 * the limit is 3 MB" hands them a problem they have to leave the app to solve,
 * for a file the browser can shrink in a few hundred milliseconds. The only
 * honest reason to reject an image is that it cannot be made to fit at all.
 *
 * The approach: draw into a canvas at a bounded size, encode as JPEG, and step
 * quality down until the result fits. Each attempt is a real encode — there is
 * no formula from quality to bytes, because it depends entirely on the picture.
 *
 * NOTE ON TRANSPARENCY: the output is always JPEG, which has no alpha channel.
 * A PNG with transparency is composited onto white. That is correct here — a
 * broadcast hero image is rendered against an opaque notification or email
 * background anyway — but it is a real conversion, not a no-op, which is why
 * a file already under the limit is returned untouched.
 */

/** Quality ladder. Below ~0.5 the artefacts become obvious on flat colour. */
const QUALITY_STEPS = [0.9, 0.8, 0.7, 0.6, 0.5];

/**
 * Longest edge, in px, that the first attempt is bounded to.
 *
 * Broadcast images render at 1200×628, so anything beyond ~2x that is detail
 * nobody will see. Bounding before encoding is what makes the quality ladder
 * short: an 8000px photo squeezed to quality 0.5 still weighs more than the
 * same photo bounded to 1600px at quality 0.9, and looks worse.
 */
const MAX_EDGE = 1600;

/** Progressively smaller bounds, tried when the quality ladder is exhausted. */
const EDGE_STEPS = [MAX_EDGE, 1200, 900];

/** Fit `(width, height)` inside a square of `edge`, preserving aspect ratio. */
const boundedSize = (width, height, edge) => {
  const longest = Math.max(width, height);
  if (longest <= edge) return { width, height };
  const scale = edge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/** Load a File into an HTMLImageElement, revoking the object URL either way. */
const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file could not be read as an image."));
    };
    img.src = url;
  });

/** Canvas → Blob as a promise. Resolves null when the browser refuses. */
const canvasToBlob = (canvas, quality) =>
  new Promise((resolve) => {
    if (typeof canvas.toBlob !== "function") {
      resolve(null);
      return;
    }
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });

/**
 * Shrink `file` until it fits within `maxBytes`.
 *
 * @param {File} file
 * @param {number} maxBytes
 * @param {{ maxEdge?: number }} [options]
 * @returns {Promise<{ file: File, compressed: boolean, originalSize: number }>}
 * @throws {Error} Only when the image cannot be read, or cannot be brought
 *   under the limit even at the smallest bound and lowest quality.
 */
export async function compressImage(file, maxBytes, { maxEdge = MAX_EDGE } = {}) {
  const originalSize = file.size;

  // Already fine. Return the ORIGINAL, not a re-encode: needlessly rewriting a
  // compliant PNG would strip its transparency for no benefit.
  if (originalSize <= maxBytes) {
    return { file, compressed: false, originalSize };
  }

  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("This browser cannot resize images.");
  }

  // `maxEdge` always leads, then whichever standard steps are smaller. Built
  // this way rather than filtering EDGE_STEPS, which silently clamped a caller
  // asking for a bound ABOVE the default to the default — the opposite of what
  // the option name promises.
  const ladder = [maxEdge, ...EDGE_STEPS.filter((edge) => edge < maxEdge)];

  for (const edge of ladder) {
    const { width, height } = boundedSize(
      img.naturalWidth || img.width,
      img.naturalHeight || img.height,
      edge
    );
    canvas.width = width;
    canvas.height = height;
    // JPEG has no alpha, so an un-painted canvas would composite transparent
    // pixels onto black. White matches the surfaces these images render on.
    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, width, height);
    context.drawImage(img, 0, 0, width, height);

    for (const quality of QUALITY_STEPS) {
      // eslint-disable-next-line no-await-in-loop -- each attempt must be
      // measured before deciding whether another is needed; running the whole
      // ladder in parallel would encode the same image up to 15 times.
      const blob = await canvasToBlob(canvas, quality);
      if (!blob) throw new Error("This browser cannot resize images.");
      if (blob.size <= maxBytes) {
        return {
          file: new File([blob], jpegName(file.name), {
            type: "image/jpeg",
            lastModified: Date.now(),
          }),
          compressed: true,
          originalSize,
        };
      }
    }
  }

  throw new Error(
    "That image is too detailed to compress under the limit. Try a smaller one."
  );
}

/** `hero.png` → `hero.jpg`, since the bytes really are JPEG now. */
const jpegName = (name) => `${String(name || "image").replace(/\.[^.]+$/, "")}.jpg`;

